/**
 * Drawing helpers for the page's charts. These are display only: they draw a smooth line through points that the
 * page already has, and keep dragged handles in order. The page computes no forecasts (root CLAUDE.md, rule 1);
 * every number on it is written into the page as an illustration.
 */

export type Pt = [number, number];

/**
 * Tangents for a monotone cubic through the points (Fritsch–Carlson), so a rising series never overshoots and a
 * "chance by date" line never dips.
 */
function tangents(pts: Pt[]): number[] {
	const n = pts.length;
	if (n < 2) return pts.map(() => 0);
	const d: number[] = [];
	for (let i = 0; i < n - 1; i++) {
		const dx = pts[i + 1][0] - pts[i][0];
		d.push(dx === 0 ? 0 : (pts[i + 1][1] - pts[i][1]) / dx);
	}
	const m: number[] = [d[0]];
	for (let i = 1; i < n - 1; i++) m.push(d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2);
	m.push(d[n - 2]);
	for (let i = 0; i < n - 1; i++) {
		if (d[i] === 0) {
			m[i] = 0;
			m[i + 1] = 0;
			continue;
		}
		const a = m[i] / d[i];
		const b = m[i + 1] / d[i];
		const s = a * a + b * b;
		if (s > 9) {
			const t = 3 / Math.sqrt(s);
			m[i] = t * a * d[i];
			m[i + 1] = t * b * d[i];
		}
	}
	return m;
}

/** An SVG path through the points as a monotone cubic. */
export function smoothPath(pts: Pt[]): string {
	if (pts.length === 0) return "";
	if (pts.length === 1) return `M${f(pts[0][0])} ${f(pts[0][1])}`;
	const m = tangents(pts);
	let out = `M${f(pts[0][0])} ${f(pts[0][1])}`;
	for (let i = 0; i < pts.length - 1; i++) {
		const [x0, y0] = pts[i];
		const [x1, y1] = pts[i + 1];
		const h = (x1 - x0) / 3;
		out += `C${f(x0 + h)} ${f(y0 + m[i] * h)} ${f(x1 - h)} ${f(y1 - m[i + 1] * h)} ${f(x1)} ${f(y1)}`;
	}
	return out;
}

/** Points along the same monotone cubic, `per` samples per segment. */
export function sampleSmooth(pts: Pt[], per = 24): Pt[] {
	if (pts.length < 2) return pts.slice();
	const m = tangents(pts);
	const out: Pt[] = [];
	for (let i = 0; i < pts.length - 1; i++) {
		const [x0, y0] = pts[i];
		const [x1, y1] = pts[i + 1];
		const dx = x1 - x0;
		for (let k = 0; k < per; k++) {
			const t = k / per;
			const t2 = t * t;
			const t3 = t2 * t;
			const y = (2 * t3 - 3 * t2 + 1) * y0 + (t3 - 2 * t2 + t) * dx * m[i] + (-2 * t3 + 3 * t2) * y1 + (t3 - t2) * dx * m[i + 1];
			out.push([x0 + dx * t, y]);
		}
	}
	out.push(pts[pts.length - 1]);
	return out;
}

/**
 * The brand's tapered line (brand/brand-guide.md, "The mark"): a filled shape whose half-width grows from `w0` at
 * the start to `w1` at `rampAt` of the way along, then holds, with round caps.
 */
export function taperPath(samples: Pt[], w0: number, w1: number, rampAt = 0.62): string {
	const n = samples.length;
	if (n < 2) return "";
	const len: number[] = [0];
	for (let i = 1; i < n; i++) len.push(len[i - 1] + Math.hypot(samples[i][0] - samples[i - 1][0], samples[i][1] - samples[i - 1][1]));
	const total = len[n - 1] || 1;
	const width = (i: number) => {
		const u = Math.min(1, len[i] / total / rampAt);
		// Ease out, so the line thickens quickly and then settles, like the mark.
		return w0 + (w1 - w0) * (1 - (1 - u) * (1 - u));
	};
	const left: Pt[] = [];
	const right: Pt[] = [];
	for (let i = 0; i < n; i++) {
		const a = samples[Math.max(0, i - 1)];
		const b = samples[Math.min(n - 1, i + 1)];
		const dx = b[0] - a[0];
		const dy = b[1] - a[1];
		const d = Math.hypot(dx, dy) || 1;
		const nx = -dy / d;
		const ny = dx / d;
		const w = width(i);
		left.push([samples[i][0] + nx * w, samples[i][1] + ny * w]);
		right.push([samples[i][0] - nx * w, samples[i][1] - ny * w]);
	}
	const we = width(n - 1);
	const ws = width(0);
	let d = `M${f(left[0][0])} ${f(left[0][1])}`;
	for (let i = 1; i < n; i++) d += `L${f(left[i][0])} ${f(left[i][1])}`;
	// Round cap at the end: an arc from the left edge to the right edge.
	d += `A${f(we)} ${f(we)} 0 0 1 ${f(right[n - 1][0])} ${f(right[n - 1][1])}`;
	for (let i = n - 2; i >= 0; i--) d += `L${f(right[i][0])} ${f(right[i][1])}`;
	d += `A${f(ws)} ${f(ws)} 0 0 1 ${f(left[0][0])} ${f(left[0][1])}Z`;
	return d;
}

/**
 * Set handle `i` to `v` and keep the series in order: a handle dragged past a neighbour pushes the neighbour along
 * rather than blocking the drag (PRD §10.3).
 */
export function setHandle(values: number[], i: number, v: number, min = 0, max = 100): number[] {
	const out = values.slice();
	const x = Math.max(min, Math.min(max, v));
	out[i] = x;
	for (let j = i + 1; j < out.length; j++) if (out[j] < x) out[j] = x;
	for (let j = i - 1; j >= 0; j--) if (out[j] > x) out[j] = x;
	return out;
}

/** Snap to a ghost value within `within` points (PRD §10.3: handles snap to a market within 2 points). */
export function snapTo(v: number, ghost: number | undefined, within = 2): number {
	return ghost !== undefined && Math.abs(v - ghost) <= within ? ghost : v;
}

const f = (n: number) => (Math.round(n * 10) / 10).toString();
